/**
 * ProfilePage - Mein Profil (Self-Service)
 * 
 * Self-Service für: Anzeigename, Vorname, Nachname
 * Nicht änderbar: Rolle, RFID/NFC, Station, E-Mail (ohne Verifikation)
 */

import { useState } from 'react';
import { Shield, KeyRound, ArrowLeft, Pencil, X, Loader2, CheckCircle, AlertCircle, User, Mail, ShieldOff, RefreshCw } from 'lucide-react';
import { UserAvatar } from '../components/UserAvatar';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { updateProfile, changeEmail, disable2FA, reconfigure2FA, ApiException } from '../api/client';

const ROLE_LABELS: Record<string, string> = {
  WORKER: 'Mitarbeiter',
  TECHNICIAN: 'Techniker',
  ADMIN: 'Administrator',
  SUPERADMIN: 'Super-Administrator',
  SYSTEM: 'System',
};

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  
  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  // 2FA Modal State
  const [twoFAModalOpen, setTwoFAModalOpen] = useState<'disable' | 'reconfigure' | null>(null);
  const [twoFAPassword, setTwoFAPassword] = useState('');
  const [twoFASaving, setTwoFASaving] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');

  // Email Change Modal State
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-500">Nicht angemeldet</div>
      </div>
    );
  }

  const openEditModal = () => {
    setEditDisplayName(user.displayName);
    setEditFirstName(user.firstName || '');
    setEditLastName(user.lastName || '');
    setError('');
    setSuccess('');
    setEditModalOpen(true);
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    // Client-side Validation
    const trimmedDisplayName = editDisplayName.trim();
    if (!trimmedDisplayName) {
      setError('Anzeigename darf nicht leer sein');
      return;
    }
    if (trimmedDisplayName.length > 128) {
      setError('Anzeigename darf maximal 128 Zeichen haben');
      return;
    }

    setSaving(true);

    try {
      await updateProfile({
        displayName: trimmedDisplayName,
        firstName: editFirstName.trim() || undefined,
        lastName: editLastName.trim() || undefined,
      });

      setSuccess('Profil aktualisiert');
      
      // User-Daten neu laden
      await refresh();
      
      // Modal nach kurzer Verzögerung schließen
      setTimeout(() => {
        setEditModalOpen(false);
        setSuccess('');
      }, 1000);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.data.error);
      } else {
        setError('Fehler beim Speichern');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <User className="w-7 h-7" />
            Mein Profil
          </h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border">
          {/* Profilbild / Avatar + Edit Button */}
          <div className="p-6 border-b flex items-center gap-4">
            <UserAvatar name={user.displayName} size="lg" />
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-slate-900">
                {user.displayName}
              </h2>
              <p className="text-sm text-slate-500">
                {user.firstName || user.lastName 
                  ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                  : ROLE_LABELS[user.role] || user.role}
              </p>
            </div>
            <button
              onClick={openEditModal}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              title="Profil bearbeiten"
            >
              <Pencil className="w-5 h-5" />
            </button>
          </div>

          {/* Daten-Felder */}
          <div className="divide-y">
            {/* Vorname */}
            <div className="p-4 flex items-center gap-4">
              <User className="w-5 h-5 text-slate-400" />
              <div className="flex-1">
                <div className="text-xs text-slate-400 uppercase tracking-wide">Vorname</div>
                <div className="text-slate-900 font-medium">
                  {user.firstName || <span className="text-slate-400">–</span>}
                </div>
              </div>
            </div>

            {/* Nachname */}
            <div className="p-4 flex items-center gap-4">
              <User className="w-5 h-5 text-slate-400" />
              <div className="flex-1">
                <div className="text-xs text-slate-400 uppercase tracking-wide">Nachname</div>
                <div className="text-slate-900 font-medium">
                  {user.lastName || <span className="text-slate-400">–</span>}
                </div>
              </div>
            </div>

            {/* E-Mail */}
            <div className="p-4 flex items-center gap-4">
              <Mail className="w-5 h-5 text-slate-400" />
              <div className="flex-1">
                <div className="text-xs text-slate-400 uppercase tracking-wide">E-Mail</div>
                <div className="text-slate-900 font-medium">
                  {user.email || <span className="text-slate-400">&ndash;</span>}
                </div>
              </div>
              <button
                onClick={() => {
                  setEditEmail(user.email || '');
                  setEmailError('');
                  setEmailSuccess('');
                  setEmailModalOpen(true);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Aendern
              </button>
            </div>

            {/* Rolle (read-only) */}
            <div className="p-4 flex items-center gap-4">
              <Shield className="w-5 h-5 text-slate-400" />
              <div className="flex-1">
                <div className="text-xs text-slate-400 uppercase tracking-wide">Rolle</div>
                <div className="text-slate-900 font-medium">
                  {ROLE_LABELS[user.role] || user.role}
                </div>
              </div>
            </div>

            {/* 2FA Status */}
            <div className="p-4 flex items-center gap-4">
              <KeyRound className="w-5 h-5 text-slate-400" />
              <div className="flex-1">
                <div className="text-xs text-slate-400 uppercase tracking-wide">
                  Zwei-Faktor-Authentifizierung
                </div>
                <div className="text-slate-900 font-medium">
                  {user.twoFactorEnabled ? (
                    <span className="text-green-600">Aktiviert</span>
                  ) : (
                    <span className="text-amber-600">Nicht aktiviert</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {user.twoFactorEnabled ? (
                  <button
                    onClick={() => {
                      setTwoFAModalOpen('reconfigure');
                      setTwoFAPassword('');
                      setTwoFAError('');
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Neu einrichten
                  </button>
                ) : (
                  <Link
                    to="/2fa/setup"
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Einrichten
                  </Link>
                )}
              </div>
            </div>

            {/* Station (falls vorhanden) */}
            {user.stationId && (
              <div className="p-4 flex items-center gap-4">
                <div className="w-5 h-5 text-slate-400 flex items-center justify-center">
                  <span className="text-xs font-bold">ST</span>
                </div>
                <div className="flex-1">
                  <div className="text-xs text-slate-400 uppercase tracking-wide">
                    Aktive Station
                  </div>
                  <div className="text-slate-900 font-medium">
                    {user.stationType || 'Unbekannt'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Hinweis */}
          <div className="p-4 bg-slate-50 border-t text-sm text-slate-500">
            Sie koennen Ihren Anzeigenamen, Vor- und Nachnamen selbst aendern.
            E-Mail-Aenderungen erfordern eine Bestaetigung per Link.
            Rolle und Stationszuordnung koennen nur durch einen Administrator geaendert werden.
          </div>
        </div>
      </main>

      {/* Email Change Modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !emailSaving && setEmailModalOpen(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">E-Mail aendern</h2>
              <button
                onClick={() => !emailSaving && setEmailModalOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded"
                disabled={emailSaving}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {emailError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{emailError}</span>
                </div>
              )}
              {emailSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{emailSuccess}</span>
                </div>
              )}
              {!emailSuccess && (
                <>
                  <p className="text-sm text-slate-500">
                    Geben Sie Ihre neue E-Mail-Adresse ein. Sie erhalten eine Bestaetigungsmail an die neue Adresse.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Neue E-Mail-Adresse
                    </label>
                    <input
                      type="email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="name@beispiel.de"
                      disabled={emailSaving}
                      autoFocus
                    />
                  </div>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setEmailModalOpen(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
                disabled={emailSaving}
              >
                {emailSuccess ? 'Schliessen' : 'Abbrechen'}
              </button>
              {!emailSuccess && (
                <button
                  onClick={async () => {
                    setEmailError('');
                    setEmailSaving(true);
                    try {
                      await changeEmail(editEmail);
                      setEmailSuccess('Bestaetigungsmail gesendet. Bitte pruefen Sie Ihr Postfach.');
                    } catch (err) {
                      if (err instanceof ApiException) {
                        setEmailError(err.data.error);
                      } else {
                        setEmailError('Fehler beim Senden');
                      }
                    } finally {
                      setEmailSaving(false);
                    }
                  }}
                  disabled={emailSaving || !editEmail.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {emailSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Bestaetigungsmail senden
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => !saving && setEditModalOpen(false)} 
          />
          
          {/* Modal */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Profil bearbeiten</h2>
              <button
                onClick={() => !saving && setEditModalOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded"
                disabled={saving}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{success}</span>
                </div>
              )}

              {/* Anzeigename */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Anzeigename <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Max Mustermann"
                  maxLength={128}
                  disabled={saving}
                />
              </div>

              {/* Vorname */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Vorname
                </label>
                <input
                  type="text"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Max"
                  maxLength={64}
                  disabled={saving}
                />
              </div>

              {/* Nachname */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nachname
                </label>
                <input
                  type="text"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Mustermann"
                  maxLength={64}
                  disabled={saving}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
                disabled={saving}
              >
                Abbrechen
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editDisplayName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2FA Passwort-Bestaetigung Modal */}
      {twoFAModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !twoFASaving && setTwoFAModalOpen(null)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                {twoFAModalOpen === 'disable' ? '2FA deaktivieren' : '2FA neu einrichten'}
              </h2>
              <button
                onClick={() => !twoFASaving && setTwoFAModalOpen(null)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded"
                disabled={twoFASaving}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {twoFAError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{twoFAError}</span>
                </div>
              )}
              <p className="text-sm text-slate-600">
                {twoFAModalOpen === 'disable'
                  ? 'Bitte bestaetigen Sie mit Ihrem Passwort, dass Sie die Zwei-Faktor-Authentifizierung deaktivieren moechten.'
                  : 'Bitte bestaetigen Sie mit Ihrem Passwort. Danach wird ein neuer QR-Code generiert.'}
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Passwort
                </label>
                <input
                  type="password"
                  value={twoFAPassword}
                  onChange={(e) => setTwoFAPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ihr aktuelles Passwort"
                  disabled={twoFASaving}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && twoFAPassword.trim()) {
                      e.preventDefault();
                      document.getElementById('2fa-confirm-btn')?.click();
                    }
                  }}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setTwoFAModalOpen(null)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
                disabled={twoFASaving}
              >
                Abbrechen
              </button>
              <button
                id="2fa-confirm-btn"
                onClick={async () => {
                  setTwoFAError('');
                  setTwoFASaving(true);
                  try {
                    if (twoFAModalOpen === 'disable') {
                      await disable2FA(twoFAPassword);
                      setTwoFAModalOpen(null);
                      await refresh();
                    } else {
                      await reconfigure2FA(twoFAPassword);
                      setTwoFAModalOpen(null);
                      navigate('/2fa/setup?returnTo=/profile');
                    }
                  } catch (err) {
                    if (err instanceof ApiException) {
                      setTwoFAError(err.data.error);
                    } else {
                      setTwoFAError('Fehler bei der Verarbeitung');
                    }
                  } finally {
                    setTwoFASaving(false);
                  }
                }}
                disabled={twoFASaving || !twoFAPassword.trim()}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                  twoFAModalOpen === 'disable'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {twoFASaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {twoFAModalOpen === 'disable' ? 'Deaktivieren' : 'Neu einrichten'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
